import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NumberUtil from 'utils/number';
import type { CountryDataItem } from 'app/modules/analytics/components/shared/charts';
import type { CountryLegendItem } from 'app/modules/analytics/components/shared/charts/components/CountryLegend';
import type { AudienceResponse } from 'api/new-analytics/types';
import { useAudienceQuery } from './useAudienceQuery';

const AUDIENCE_LEGEND_TOP_COUNT = 4;

interface BuildAudienceLocationDisplayParams {
  audience: AudienceResponse | undefined;
  isAwaitingAudienceData: boolean;
  otherLabel: string;
}

export function buildAudienceLocationDisplay({
  audience,
  isAwaitingAudienceData,
  otherLabel,
}: BuildAudienceLocationDisplayParams) {
  const geo = audience?.geo ?? [];
  const mapData: CountryDataItem[] = geo.map((g) => ({
    countryCode: g.country_code,
    value: g.value,
  }));
  const top = geo.slice(0, AUDIENCE_LEGEND_TOP_COUNT);
  const rest = geo.slice(AUDIENCE_LEGEND_TOP_COUNT);
  const otherPercent = rest.reduce((sum, g) => sum + g.percent, 0);
  const legendItems: CountryLegendItem[] = top.map((g) => ({
    countryCode: g.country_code,
    label: g.country_name,
    percent: g.percent,
  }));
  if (rest.length > 0 && otherPercent > 0) {
    legendItems.push({
      countryCode: 'OTHER',
      label: otherLabel,
      percent: Math.round(otherPercent * 10) / 10,
    });
  }
  let totalLabel = '—';
  if (isAwaitingAudienceData) {
    totalLabel = '…';
  } else if (audience !== undefined) {
    totalLabel = NumberUtil.compact(audience.total);
  }
  return {
    mapData,
    legendItems,
    totalLabel,
    isLoading: isAwaitingAudienceData,
    isEmpty: mapData.length === 0,
  };
}

export function useAudienceLocationDisplay() {
  const { t } = useTranslation();
  const { data: audience, isFetching } = useAudienceQuery();

  const isAwaitingAudienceData = isFetching && audience === undefined;

  const display = useMemo(
    () =>
      buildAudienceLocationDisplay({
        audience,
        isAwaitingAudienceData,
        otherLabel: t('analyticsPerformance.metrics.other'),
      }),
    [audience, isAwaitingAudienceData, t],
  );

  return display;
}

export type AudienceLocationDisplay = ReturnType<
  typeof buildAudienceLocationDisplay
>;
